import type React from "react";
import ClockIcon from "../assets/svgs/common/clock.svg";
// import MiniStats from "./mini-stats.components";
import { Button } from "./ui/button";
import ArrowTopRightIcon from "../assets/svgs/common/arrow-top-right.svg";
import {
  ActiveBadge,
  ClosedBadge,
  ReelsBadge,
  ShortsBadge,
  UnderReviewBadge,
  VideoBadge,
} from "./badges.components";
import {
  CampaignStatus,
  SocialPostType,
  type ICampaign,
} from "../types/models/campaign.models.types";
import { Link } from "react-router-dom";
import MiniStats from "./mini-stats.components";
import millify from "millify";
import AddImageIcon from "../assets/svgs/common/add-image.svg";
import { DateTime } from "luxon";
import { useUserStore } from "../store/user.store";
import { AccountType } from "../constants/account-types.constants";
import LockIcon from "../assets/svgs/common/lock.svg";
import { SocialPlatforms } from "../types/models/social.models.types";

interface AppProps {
  campaign: ICampaign;
}

const CampaignCard: React.FunctionComponent<AppProps> = ({ campaign }) => {
  const { user } = useUserStore();

  function getActionPathAndTitle() {
    if (campaign.campaignStatus.status === CampaignStatus.live) {
      return {
        path: `/${user?.accountType}/campaigns/${campaign.id}/campaign-metrics`,
        title: (
          <Button className="flex items-center gap-1 bg-tertiarybuttonbg px-9 py-6 border border-border rounded-lg w-full font-semibold text-base text-secondarybuttontext tracking-wider">
            <>
              View campaign{" "}
              <span className="text-2xl">
                <ArrowTopRightIcon />
              </span>
            </>
          </Button>
        ),
      };
    }
    if (campaign.campaignStatus.status === CampaignStatus.adminDraft) {
      if (user?.accountType === AccountType.BRAND) {
        return {
          path: "",
          title: (
            <Button className="flex items-center gap-1 bg-tertiarybuttonbg px-9 py-6 border border-border border-dashed rounded-lg w-full font-semibold text-base text-secondarybuttontext tracking-wider cursor-not-allowed">
              <div className="flex items-center gap-2">
                <LockIcon /> In-review
              </div>
            </Button>
          ),
        };
      }
      if (user?.accountType === AccountType.ADMIN) {
        return {
          path: `/${user?.accountType}/campaigns/${campaign.id}/edit`,
          title: (
            <Button className="flex items-center gap-1 bg-tertiarybuttonbg px-9 py-6 border border-border rounded-lg w-full font-semibold text-base text-secondarybuttontext tracking-wider">
              <>
                Review
                <span className="text-2xl">
                  <ArrowTopRightIcon />
                </span>
              </>
            </Button>
          ),
        };
      }
    }
    if (campaign.campaignStatus.status === CampaignStatus.ended) {
      return {
        path: `/${user?.accountType}/campaigns/${campaign.id}/campaign-metrics`,
        title: (
          <Button className="flex items-center gap-1 bg-tertiarybuttonbg px-9 py-6 border border-border rounded-lg w-full font-semibold text-base text-secondarybuttontext tracking-wider">
            <>
              View campaign{" "}
              <span className="text-2xl">
                <ArrowTopRightIcon />
              </span>
            </>
          </Button>
        ),
      };
    }
    if (campaign.campaignStatus.status === CampaignStatus.brandDraft) {
      return {
        path: `/${user?.accountType}/campaigns/${campaign.id}/edit`,
        title: "",
      };
    }

    throw new Error("unknown campaign status");
  }
  const actionInfo = getActionPathAndTitle();

  const calculateRemainingTime = (
    startDate: string,
    endDate: string,
    currentDate: string,
  ): string => {
    const start = DateTime.fromISO(startDate);
    const end = DateTime.fromISO(endDate);
    const current = DateTime.fromISO(currentDate);

    if (end < current) {
      return "";
    }

    if (start > current) {
      return `starts ${start.toRelative()}`;
    }

    const diff = end.diff(current, ["days", "hours", "minutes", "seconds"]);

    if (diff.days >= 1) {
      return `${diff.days.toFixed(0)} days left`;
    }
    if (diff.hours >= 1) {
      return `${diff.hours.toFixed(0)} hours left`;
    }
    if (diff.minutes >= 1) {
      return `${diff.minutes.toFixed(0)} minutes left`;
    }
    return `${diff.seconds.toFixed(0)} seconds left`;
  };

  return (
    <div className="flex items-center w-full">
      <div className="flex flex-col gap-2 pb-5 border border-border rounded-10 w-full overflow-hidden">
        <div className="relative rounded-b-10 w-full overflow-hidden aspect-video">
          <div
            style={{
              background:
                "linear-gradient(180deg, #131313 0%, rgba(19, 19, 19, 0.64) 50.5%, rgba(19, 19, 19, 0.00) 100%)",
            }}
            className="absolute w-full h-20"
          />
          <span className="top-3 left-0 absolute flex items-center gap-1 bg-badgebg backdrop-blur-sm px-4 py-2 pl-6 border-border border-t border-r border-b rounded-r-full font-bold text-sm">
            by {campaign.brand.name}
          </span>
          <span className="top-3 right-3 absolute flex items-center gap-1 bg-badgebg backdrop-blur-sm px-2 py-2 border border-border rounded-full font-bold text-sm">
            <ClockIcon />
            {campaign.startDate &&
              campaign.endDate &&
              calculateRemainingTime(
                campaign.startDate.toString(),
                campaign.endDate.toString(),
                DateTime.now().toISO(),
              )}
          </span>
          {campaign.coverImage?.url ? (
            <img
              src={campaign.coverImage?.url}
              className="border-border border-b rounded-b-xl w-full h-full aspect-video object-cover"
              alt=""
            />
          ) : (
            <div className="top-0 left-0 absolute flex justify-center items-center bg-opacity-50 border-border border-b rounded-xl w-full h-full">
              <AddImageIcon />
            </div>
          )}
        </div>
        <div className="flex flex-col gap-4 px-4">
          <div className="flex flex-col gap-2">
            <p className="w-80 font-semibold text-xl truncate">
              {campaign.name}
            </p>
            <div className="flex items-center gap-1">
              {campaign.campaignStatus.status === CampaignStatus.live ? (
                <ActiveBadge />
              ) : (
                <></>
              )}
              {campaign.campaignStatus.status === CampaignStatus.adminDraft ? (
                <UnderReviewBadge />
              ) : (
                <></>
              )}
              {campaign.campaignStatus.status === CampaignStatus.ended ? (
                <ClosedBadge />
              ) : (
                <></>
              )}
              {campaign.socialPlatform === "instagram" && <ReelsBadge />}
              {campaign.socialPlatform === SocialPlatforms.youtube &&
                campaign.socialPostType === SocialPostType.YOUTUBE_SHORT && (
                  <ShortsBadge />
                )}
              {campaign.socialPlatform === SocialPlatforms.youtube &&
                campaign.socialPostType === SocialPostType.YOUTUBE_VIDEO && (
                  <VideoBadge />
                )}
            </div>
          </div>
          <div className="">
            <MiniStats
              campaign={campaign}
              status={campaign.campaignStatus.status}
              stat1={millify(campaign.metrics?.totalCount || 0)}
              stat2={millify(campaign.metrics?.totalPosts || 0)}
              stat3={
                campaign.budget
                  ? `${millify(campaign.metrics?.budgetUsed || 0)} of ${millify(campaign.budget)}`
                  : "-"
              }
            />
          </div>
          <Link className="w-full" to={actionInfo.path}>
            {actionInfo.title}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CampaignCard;
